import DOMPurify from 'dompurify';

/**
 * Validates and sanitizes SVG content to prevent XSS attacks
 */
export const validateAndSanitizeSVG = async (file) => {
    try {
        const content = await file.text();

        // Check for suspicious patterns
        const dangerousPatterns = [
            /<script[\s\S]*?<\/script>/gi,
            /on\w+\s*=\s*["'][^"']*["']/gi, // Event handlers like onclick, onload, etc.
            /javascript:/gi,
            /<iframe/gi,
            /<embed/gi,
            /<object/gi,
            /<applet/gi,
            /<link/gi,
            /<import/gi,
            /<meta/gi,
            /data:text\/html/gi,
        ];

        for (const pattern of dangerousPatterns) {
            if (pattern.test(content)) {
                return {
                    isValid: false,
                    error: 'File contains potentially malicious content'
                };
            }
        }

        // Use DOMPurify to sanitize SVG content
        const sanitized = DOMPurify.sanitize(content, {
            USE_PROFILES: { svg: true },
            KEEP_CONTENT: false,
            ADD_TAGS: ['svg', 'g', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'text', 'tspan'],
            ADD_ATTR: ['viewBox', 'd', 'fill', 'stroke', 'stroke-width', 'cx', 'cy', 'r', 'x', 'y', 'width', 'height', 'transform', 'points'],
            FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'style', 'applet', 'meta', 'import'],
            FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit']
        });

        return {
            isValid: true,
            sanitizedContent: sanitized
        };
    } catch (error) {
        return {
            isValid: false,
            error: 'Failed to validate SVG file'
        };
    }
};

/**
 * Validates PDF files for potential security issues
 */
export const validatePDF = async (file) => {
    try {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);

        // Check PDF header
        const header = new TextDecoder('iso-8859-1').decode(bytes.slice(0, 5));
        if (!header.startsWith('%PDF-')) {
            return {
                isValid: false,
                error: 'Invalid PDF file format'
            };
        }

        const sampleSize = Math.min(bytes.length, 10240);
        const content = String.fromCharCode(...bytes.slice(0, sampleSize));

        const dangerousPatterns = [
            /\/JavaScript/i,
            /\/JS/i,
            /\/Launch/i,
            /\/SubmitForm/i,
            /\/ImportData/i,
            /\/OpenAction/i,
            /\/AA/i, // Additional Actions
            /\/URI/i, // URIs that could be malicious
            /\/GoToR/i, // Remote go-to actions
            /\/RichMedia/i, // Flash content
            /\/XFA/i, // XFA forms can contain scripts
            /\/Annot\s+\/Subtype\s+\/Widget/i, // Interactive forms
            /\/Annot\s+\/Action/i // Annotations with actions
        ];

        for (const pattern of dangerousPatterns) {
            if (pattern.test(content)) {
                return {
                    isValid: false,
                    error: 'PDF contains potentially dangerous content'
                };
            }
        }

        return {
            isValid: true
        };
    } catch (error) {
        return {
            isValid: false,
            error: 'Failed to validate PDF file'
        };
    }
};

/**
 * Validates image files to ensure they're safe
 */
export const validateImage = async (file) => {
    try {
        if (
            file.type === 'image/tiff' ||
            file.type === 'image/x-tiff' ||
            file.name.toLowerCase().endsWith('.tif') ||
            file.name.toLowerCase().endsWith('.tiff')
        ) {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);

            const isLittleEndian = bytes[0] === 0x49 && bytes[1] === 0x49;
            const isBigEndian = bytes[0] === 0x4D && bytes[1] === 0x4D;

            if (!isLittleEndian && !isBigEndian) {
                return {
                    isValid: false,
                    error: 'Invalid TIFF file format'
                };
            }

            return { isValid: true };
        }

        // For regular images, browser's built-in image validation is usually sufficient
        // but we can add additional checks
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                resolve({ isValid: true });
            };
            img.onerror = () => {
                resolve({
                    isValid: false,
                    error: 'Invalid or corrupted image file'
                });
            };
            img.src = URL.createObjectURL(file);
        });
    } catch (error) {
        return {
            isValid: false,
            error: 'Failed to validate image file'
        };
    }
};

/**
 * Main validation function that routes to appropriate validator
 */
export const validateUploadedFile = async (file) => {
    const fileType = file.type.toLowerCase();
    const fileName = (file.name || '').toLowerCase();

    // TIFF by MIME or extension -> treat as image
    if (
        fileType === 'image/tiff' ||
        fileType === 'image/x-tiff' ||
        fileName.endsWith('.tif') ||
        fileName.endsWith('.tiff')
    ) {
        return validateImage(file);
    }
    // SVG validation
    if (fileType === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        return validateAndSanitizeSVG(file);
    }

    // PDF validation
    if (fileType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        return validatePDF(file);
    }

    // Image validation
    if (fileType.startsWith('image/')) {
        return validateImage(file);
    }

    // ZIP files should be scanned on the server-side
    if (fileType === 'application/zip' || fileType === 'application/x-zip-compressed') {
        // Client-side validation for ZIP is limited
        // Should be handled server-side
        return { isValid: false };
    }

    // Unknown file type
    return {
        isValid: false,
        error: 'Unsupported file type'
    };
};

/**
 * Creates a safe preview URL for files
 */
export const createSafePreviewUrl = (file) => {
    if (typeof file === 'string') {
        // If it's already a URL, return it (assuming it's from your server)
        return file;
    }

    // For SVG files, we should never create object URLs directly
    // Instead, convert to safe format or use sanitized content
    if (file.type === 'image/svg+xml') {
        // Return null to indicate SVG needs special handling
        return null;
    }

    // For other files, create object URL
    return URL.createObjectURL(file);
};

/**
 * Renders SVG safely by sanitizing it first
 */
export const renderSafeSVG = async (file) => {
    const validation = await validateAndSanitizeSVG(file);

    if (!validation.isValid || !validation.sanitizedContent) {
        throw new Error(validation.error || 'Invalid SVG file');
    }

    // Convert sanitized SVG to data URL
    const blob = new Blob([validation.sanitizedContent], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
};